import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { ShoppingBag, ArrowLeft, Heart, Star } from "lucide-react";
import { toast } from "sonner";
import ItemCard from "../components/ItemCard";
import RestockButton from "../components/RestockButton";

export default function ItemDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, items: cart } = useCart();
  const wl = useWishlist();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/items/${id}`).then((r) => setItem(r.data)).catch(() => nav("/shop"));
    api.get(`/items/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated([]));
    setActiveImg(0);
  }, [id, nav]);

  if (!item) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 font-display text-3xl uppercase">Loading…</div>;

  const inCart = cart.find((c) => c.id === item.id);
  const liked = wl.has(item.id);

  const handleAdd = () => {
    if (item.sold) { toast.error("That piece is already sold"); return; }
    add({ id: item.id, title: item.title, price: item.price, image: item.images?.[0] });
    toast.success("Boggy bagged it · added to cart");
  };

  const handleHeart = async () => {
    if (!user) { toast.message("Log in to save favorites"); return; }
    const added = await wl.toggle(item.id);
    toast.success(added ? "Saved to your stash" : "Removed");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="item-detail">
      <button onClick={() => nav(-1)} className="brut-btn bg-white px-4 py-2 mb-6 flex items-center gap-2" data-testid="back-btn">
        <ArrowLeft size={18} strokeWidth={3} /> back
      </button>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-7">
          <div className="brut aspect-[4/5] overflow-hidden bg-[#f3efe6] relative">
            {item.images?.[activeImg] ? (
              <img src={fileUrl(item.images[activeImg])} alt={item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-5xl text-[#1A1A1A]/30">no pic</div>
            )}
            {item.featured && (
              <span className="tag-sticker bg-pink absolute top-4 left-4 text-xl rotate-tilt-l flex items-center gap-1">
                <Star size={16} strokeWidth={3} fill="currentColor" /> Fox's pick
              </span>
            )}
          </div>
          {item.images && item.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {item.images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`brut-sm w-20 h-20 overflow-hidden ${activeImg === i ? "ring-4 ring-[#FF5A00]" : ""}`}>
                  <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-5">
          {item.sold && <span className="tag-sticker bg-pink text-2xl inline-block mb-3 -rotate-3">SOLD!</span>}
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-tight">{item.title}</h1>
          <div className="font-display text-5xl mt-3">${Number(item.price).toFixed(2)}</div>
          <div className="flex flex-wrap gap-2 mt-5 text-sm font-bold uppercase tracking-wider">
            <span className="border-2 border-[#1A1A1A] px-3 py-1">Size {item.size}</span>
            <span className="border-2 border-[#1A1A1A] px-3 py-1">{item.condition}</span>
            <span className="border-2 border-[#1A1A1A] px-3 py-1">{item.category}</span>
            {item.brand && <span className="border-2 border-[#1A1A1A] px-3 py-1">{item.brand}</span>}
            {item.color && <span className="border-2 border-[#1A1A1A] px-3 py-1">{item.color}</span>}
          </div>
          <p className="mt-6 text-lg whitespace-pre-line">{item.description}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            {item.sold ? (
              <RestockButton itemId={item.id} />
            ) : (
              <>
                <button
                  onClick={handleAdd}
                  disabled={!!inCart}
                  className={`brut-btn px-6 py-4 text-lg flex items-center gap-2 ${inCart ? "bg-green" : "bg-orange text-white"}`}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag size={20} strokeWidth={3} />
                  {inCart ? "In your cart" : "Add to cart"}
                </button>
                <button
                  onClick={() => { handleAdd(); nav("/cart"); }}
                  className="brut-btn bg-yellow px-6 py-4 text-lg"
                  data-testid="buy-now-btn"
                >
                  Buy now
                </button>
              </>
            )}
            <button
              onClick={handleHeart}
              className={`brut-btn px-4 py-4 ${liked ? "bg-pink text-white" : "bg-white"}`}
              data-testid="detail-heart-btn"
              title="Save to wishlist"
            >
              <Heart size={20} strokeWidth={3} fill={liked ? "currentColor" : "none"} />
            </button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16" data-testid="related-section">
          <span className="tag-sticker bg-cyan text-xl rotate-tilt-l inline-block mb-2">curator picks</span>
          <h2 className="font-display text-3xl md:text-4xl uppercase mb-6">You might also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {related.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
